import { onBeforeUnmount } from 'vue'
import ImpressionObserver, { getSharedImpressionObserver } from 'components/impression_observer'

// Vue composable 版的曝光偵測，內部使用 components/impression_observer 的核心邏輯。
// 用法（v-for 列表，key 用該項目穩定不變的 id，不要用陣列 index）：
//   const { bindImpression } = useModuleImpression()
//   <div v-for="post in posts" :key="post.id"
//        :ref="el => bindImpression(post.id, el, { page_type, module, target_type, target_id, position })">
//
// 用 key 而不是只用 element 追蹤，是因為 v-for 列表項目會被整批換掉（篩選條件變更、重新查詢），
// Vue 在項目從列表移除時會呼叫 bindImpression(key, null, payload)，
// 這時才知道「哪個 key 對應的舊元素」該取消監看；只認 element 的話，
// 元素消失後就找不到它是誰了，監看會一直留著沒清掉。
//
// options.sampleEvery（預設 1 = 每一項都追蹤）：列表很長時（無限捲動可能幾十上百項），
// 不需要每一項各自的曝光數字，只需要「大家普遍捲到多深」這個里程碑訊號。
// 傳大於 1 的值，只在 payload.position 是 sampleEvery 的倍數時才掛監看（position 0 一定會被取樣，
// 當作第一屏的基準點），大幅減少事件量。
export function useModuleImpression(options = {}) {
  const hasCustomOptions = 'threshold' in options || 'dwellMs' in options
  const observer = hasCustomOptions
    ? new ImpressionObserver(options)
    : getSharedImpressionObserver()

  const sampleEvery = options.sampleEvery || 1

  const elementsByKey = new Map()

  const bindImpression = (key, element, payload) => {
    const previousElement = elementsByKey.get(key)
    if (previousElement && previousElement !== element) {
      observer.unobserve(previousElement)
      elementsByKey.delete(key)
    }

    if (!element) return
    if (sampleEvery > 1 && payload.position % sampleEvery !== 0) return

    observer.observe(element, () => {
      if (window.trackingEvent) {
        window.trackingEvent.track_module_impression(payload)
      }
    })
    elementsByKey.set(key, element)
  }

  const unbindImpression = (key) => {
    const element = elementsByKey.get(key)
    if (!element) return

    observer.unobserve(element)
    elementsByKey.delete(key)
  }

  onBeforeUnmount(() => {
    elementsByKey.forEach((element) => observer.unobserve(element))
    elementsByKey.clear()
  })

  return {
    bindImpression,
    unbindImpression,
  }
}
